import { useState, useEffect, useMemo } from "react";
import AppBreadcrumbs from "../../../components/AppBreadcrumbs";
import * as services from "../../../services/saleng";
import { useAuth } from "../../../contexts/AuthContext";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import SearchIcon from "@mui/icons-material/Search";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { getSaleItemSummary } from "../../../utils/salePricing";

const TAX_RATE = 0.07;

const num = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const getDestinationSummary = (item) => {
  const entries = Array.isArray(item?.destinationEntries)
    ? item.destinationEntries
    : [];

  if (entries.length === 0) {
    const weight = num(item?.netWeight ?? item?.weight);
    const pricePerUnit = num(item?.pricePerUnit);
    return { weight, pricePerUnit, amount: weight * pricePerUnit };
  }

  const weight = entries.reduce((sum, entry) => sum + num(entry?.weight), 0);
  const amount = entries.reduce(
    (sum, entry) =>
      sum + num(entry?.weight) * num(entry?.pricePerUnit),
    0
  );

  return {
    weight,
    pricePerUnit: weight > 0 ? amount / weight : 0,
    amount,
  };
};

const getDestinationDetailRows = (item) => {
  const entries = Array.isArray(item?.destinationEntries)
    ? item.destinationEntries
    : [];
  if (entries.length === 0) return [item];

  const destinationWeight = entries.reduce(
    (sum, entry) => sum + num(entry?.weight),
    0
  );
  const destinationAmount = entries.reduce(
    (sum, entry) =>
      sum + num(entry?.weight) * num(entry?.pricePerUnit),
    0
  );

  return entries.map((entry, index) => {
    const weight = num(entry?.weight);
    const amount = weight * num(entry?.pricePerUnit);
    const allocation =
      destinationWeight > 0 ? weight / destinationWeight : 1 / entries.length;
    const vatAllocation =
      destinationAmount > 0 ? amount / destinationAmount : allocation;
    const sendWeight = num(item?.sendWeight) * allocation;
    const costAmount = num(item?.costAmount) * allocation;
    const vat = num(item?.vat) * vatAllocation;

    return {
      ...item,
      detailKey: entry?.id ?? `${item?.id}-destination-${index}`,
      productName: entry?.name || item?.productName || "-",
      sourceProductName: item?.productName || "-",
      sendWeight,
      netWeight: weight,
      confirmPricePerUnit: num(entry?.pricePerUnit),
      amountBeforeVat: amount,
      vat,
      netPrice: amount + vat,
      costAmount,
      profitBeforeTax: amount - costAmount,
    };
  });
};

const STATUS_OPTIONS = [
  { label: "ส่งแล้ว", value: "sent" },
  { label: "ยืนยันแล้ว", value: "confirm" },
  { label: "รับเงินแล้ว", value: "payed" },
];

export default function SummaryTicketSales() {
  const auth = useAuth();
  const formatDate = (date) => date.toISOString().split("T")[0];

  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    date.setMonth(date.getMonth() - 1);
    return formatDate(date);
  });
  const [endDate, setEndDate] = useState(formatDate(new Date()));
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [openSet, setOpenSet] = useState(() => new Set());
  const [search, setSearch] = useState("");
  const [invById, setInvById] = useState({});
  const [invByName, setInvByName] = useState({});

  // sort state
  const [orderBy, setOrderBy] = useState("createdAt");
  const [order, setOrder] = useState("desc");

  // status filter state
  const [selectedStatus, setSelectedStatus] = useState(STATUS_OPTIONS[0].value);

  const canSeeCost = useMemo(
    () => auth?.permissions?.report_show_cost === true,
    [auth]
  );
  const canSeeProfit = useMemo(
    () => auth?.permissions?.report_show_profit === true,
    [auth]
  );

  useEffect(() => {
    const loadInventory = async () => {
      try {
        const response = await services.getInventory();
        const inventory = response?.data?.data ?? response?.data ?? response ?? [];
        const byId = {};
        const byName = {};

        (Array.isArray(inventory) ? inventory : [inventory]).forEach((record) => {
          const averageCost = num(
            record?.avgCostPerUnit ??
              record?.averageCost ??
              record?.avgCost ??
              record?.costPerUnit ??
              record?.cost
          );
          const productId =
            record?.productId ?? record?.product?.id ?? record?.product_id;
          const productName =
            record?.productName ??
            record?.product?.title ??
            record?.name ??
            record?.product_name;

          if (productId != null) byId[String(productId)] = averageCost;
          if (productName) byName[String(productName).trim()] = averageCost;
        });

        setInvById(byId);
        setInvByName(byName);
      } catch (error) {
        console.error("loadInventory error", error);
        setInvById({});
        setInvByName({});
      }
    };

    loadInventory();
  }, []);

  // ---------- Load sales data ----------
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const res = await services.getSaleTransactions({
          ...(selectedStatus ? { status: selectedStatus } : {}),
          startDate,
          endDate,
        });

        const rawData = res?.data?.data ?? res?.data ?? res ?? [];

        const processed = (Array.isArray(rawData) ? rawData : [rawData]).map(
          (row) => {
            const isVat = row?.isVat === true;
            const historicalCostByProduct = {};

            (row.InventoryTransaction ?? []).forEach((transaction) => {
              const key = String(transaction.productId);
              if (!historicalCostByProduct[key]) {
                historicalCostByProduct[key] = { quantity: 0, totalCost: 0 };
              }
              historicalCostByProduct[key].quantity += num(transaction.quantity);
              historicalCostByProduct[key].totalCost += num(
                transaction.totalCost ??
                  num(transaction.quantity) * num(transaction.unitCost)
              );
            });

            const items = (row.items ?? []).map((item) => {
              const productName = String(
                item.productName ?? item.product?.title ?? "-"
              ).trim();
              const productId = item.productId ?? item.product?.id;
              const sendWeight = num(item.netWeight ?? item.weight);
              const saleSummary = getSaleItemSummary(item);
              const pricePerUnit = saleSummary.pricePerUnit;
              const destination = getDestinationSummary(item);
              const historicalCost =
                productId != null
                  ? historicalCostByProduct[String(productId)]
                  : undefined;
              const averageCost = num(
                item.costPerUnit ??
                  (historicalCost?.quantity > 0
                    ? historicalCost.totalCost / historicalCost.quantity
                    : undefined) ??
                  (productId != null ? invById[String(productId)] : undefined) ??
                  invByName[productName]
              );
              const costAmount = averageCost * sendWeight;
              const amountBeforeVat = saleSummary.amount;

              // VAT ต่อรายการ: ถ้าบิลมี vat รวม จะไปกระจายทีหลัง
              const itemVat =
                !isVat ? 0 :
                  row.vat != null ? 0 :
                    amountBeforeVat * TAX_RATE;

              return {
                ...item,
                productName,
                netWeight: destination.weight,
                sendWeight,
                pricePerUnit,
                confirmPricePerUnit: destination.pricePerUnit,
                amountBeforeVat,
                averageCost,
                costAmount,
                profitBeforeTax: amountBeforeVat - costAmount,
                vat: itemVat,
                netPrice: amountBeforeVat + itemVat,
              };
            });

            // กระจาย VAT ระดับบิล
            if (isVat && row.vat != null && items.length) {
              const totalBefore = items.reduce(
                (acc, it) => acc + Number(it.amountBeforeVat || 0),
                0
              );
              const totalVatFromRow = Number(row.vat ?? 0);
              items.forEach((it) => {
                const share =
                  totalBefore > 0
                    ? (Number(it.amountBeforeVat || 0) / totalBefore) * totalVatFromRow
                    : 0;
                it.vat = share;
                it.netPrice = Number(it.amountBeforeVat || 0) + share;
              });
            }

            return { ...row, items };
          }
        );

        setRows(processed);
        setOpenSet(new Set());
      } catch (err) {
        console.error("fetch error", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [startDate, endDate, selectedStatus, invById, invByName]);

  // ---------- Utils ----------
  const formatNum = (val) =>
    val != null
      ? Number(val).toLocaleString("th-TH", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
      : "-";

  const formatAverageCost = (val) =>
    val != null
      ? Number(val).toLocaleString("th-TH", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 5,
        })
      : "-";

  const formatDateTime = (datetimeStr) => {
    const date = new Date(datetimeStr);
    return date.toLocaleDateString("th-TH", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatThaiDate = (datetimeStr) => {
    const date = new Date(datetimeStr);
    return date.toLocaleDateString("th-TH", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  // ---------- Row metrics (sum จาก sub items) ----------
  const getRowMetrics = (row) => {
    const weightSend =
      row.items?.reduce(
        (acc, it) => acc + num(it.sendWeight),
        0
      ) ?? 0;

    const weightDest =
      row.items?.reduce(
        (acc, it) => acc + num(it.netWeight),
        0
      ) ?? 0;

    const weightDeduct =
      row.items?.reduce((acc, it) => {
        const sendW = num(it.sendWeight);
        const destW = num(it.netWeight);
        return acc + (destW - sendW);
      }, 0) ?? 0;

    const beforeVat =
      row.items?.reduce(
        (acc, it) => acc + Number(it.amountBeforeVat ?? 0),
        0
      ) ?? 0;

    const vat =
      row.items?.reduce(
        (acc, it) => acc + Number(it.vat ?? 0),
        0
      ) ?? 0;

    const grandTotal = beforeVat + vat;
    const sendAmount =
      row.items?.reduce(
        (acc, it) => acc + num(it.sendWeight) * num(it.pricePerUnit),
        0
      ) ?? 0;
    const costAmount =
      row.items?.reduce((acc, it) => acc + num(it.costAmount), 0) ?? 0;
    const profitBeforeTax = beforeVat - costAmount;
    const averageCost = weightSend > 0 ? costAmount / weightSend : 0;
    const salePricePerUnit = weightSend > 0 ? sendAmount / weightSend : 0;
    const confirmPricePerUnit = weightDest > 0 ? beforeVat / weightDest : 0;

    return {
      weightSend,    // น้ำหนักส่งสุทธิ (หลัก) = sum(sub)
      weightDeduct,  // น้ำหนักหัก (หลัก) = sum(dest - send) ของ sub
      weightDest,    // น้ำหนักปลายทางสุทธิ (หลัก) = sum(sub)
      beforeVat,
      vat,
      grandTotal,
      costAmount,
      profitBeforeTax,
      averageCost,
      sendAmount,
      salePricePerUnit,
      confirmPricePerUnit,
    };
  };

  // ---------- Search ----------
  const filteredRows = useMemo(() => {
    const q = (search || "").toLowerCase().trim();
    if (!q) return rows;

    return rows.filter((row) => {
      const bill = row.billNumber?.toLowerCase() || "";
      const customer =
        row.customer?.name?.toLowerCase() ||
        row.customerName?.toLowerCase() ||
        "";
      const dateTH = formatThaiDate(row.createdAt).toLowerCase();
      const dateISO = String(row.createdAt || "").toLowerCase();
      const productList = (row.items || [])
        .map((it) => (it.productName || "").toLowerCase())
        .join(" ");

      return (
        bill.includes(q) ||
        customer.includes(q) ||
        dateTH.includes(q) ||
        dateISO.includes(q) ||
        productList.includes(q)
      );
    });
  }, [rows, search]);

  // ---------- Sort ----------
  const sortedRows = useMemo(() => {
    const data = [...filteredRows];
    data.sort((a, b) => {
      const mA = getRowMetrics(a);
      const mB = getRowMetrics(b);

      let aVal;
      let bVal;

      switch (orderBy) {
        case "billNumber":
          aVal = a.billNumber ?? "";
          bVal = b.billNumber ?? "";
          break;
        case "createdAt":
          aVal = new Date(a.createdAt).getTime() || 0;
          bVal = new Date(b.createdAt).getTime() || 0;
          break;
        case "customerName":
          aVal = (a.customer?.name ?? a.customerName ?? "").toLowerCase();
          bVal = (b.customer?.name ?? b.customerName ?? "").toLowerCase();
          break;
        case "weightSend":
          aVal = mA.weightSend;
          bVal = mB.weightSend;
          break;
        case "weightDeduct":
          aVal = mA.weightDeduct;
          bVal = mB.weightDeduct;
          break;
        case "weightDest":
          aVal = mA.weightDest;
          bVal = mB.weightDest;
          break;
        case "beforeVat":
          aVal = mA.beforeVat;
          bVal = mB.beforeVat;
          break;
        case "vat":
          aVal = mA.vat;
          bVal = mB.vat;
          break;
        case "grandTotal":
          aVal = mA.grandTotal;
          bVal = mB.grandTotal;
          break;
        case "costAmount":
          aVal = mA.costAmount;
          bVal = mB.costAmount;
          break;
        case "profitBeforeTax":
          aVal = mA.profitBeforeTax;
          bVal = mB.profitBeforeTax;
          break;
        default:
          aVal = 0;
          bVal = 0;
      }

      if (typeof aVal === "string" && typeof bVal === "string") {
        const cmp = aVal.localeCompare(bVal, "th");
        return order === "asc" ? cmp : -cmp;
      } else {
        const cmp = (aVal || 0) - (bVal || 0);
        return order === "asc" ? cmp : -cmp;
      }
    });
    return data;
  }, [filteredRows, orderBy, order]);

  const requestSort = (key) => {
    if (orderBy === key) {
      setOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setOrderBy(key);
      if (["billNumber", "customerName"].includes(key)) setOrder("asc");
      else setOrder("desc");
    }
  };

  const getRowKey = (row, idx) =>
    String(row.transactionId ?? row.id ?? row.billNumber ?? idx);

  const toggleOpen = (key) => {
    setOpenSet((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // ---------- Export (main) ----------
  function exportToCSV(data, filename = "sales-summary.csv") {
    if (!data || data.length === 0) return;

    const headers = Object.keys(data[0]);
    const rows = data.map((row) =>
      headers.map((field) => `"${row[field] ?? ""}"`).join(",")
    );

    const csv = [headers.join(","), ...rows].join("\n");
    const blob = new Blob(["\uFEFF" + csv], {
      type: "text/csv;charset=utf-8;",
    });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  const handleExportMain = () => {
    const dataset = sortedRows.map((row) => {
      const {
        weightSend,
        weightDeduct,
        weightDest,
        beforeVat,
        vat,
        grandTotal,
        costAmount,
        profitBeforeTax,
        averageCost,
        salePricePerUnit,
        confirmPricePerUnit,
      } = getRowMetrics(row);
      const orderedBy =
        row.openByNames ??
        row.orderedBy?.name ??
        row.createdBy?.name ??
        row.shop?.name ??
        "-";

      return {
        เลขที่ตั๋ว: row.billNumber ?? "",
        วันที่จ่าย: formatDateTime(row.createdAt),
        ชื่อลูกค้า: row.customer?.name ?? row.customerName ?? "-",
        สั่งจ่ายโดย: orderedBy,
        "น้ำหนักส่งสุทธิ": Number(weightSend).toFixed(2),
        "น้ำหนักหัก": Number(weightDeduct).toFixed(2),
        "น้ำหนักปลายทางสุทธิ": Number(weightDest).toFixed(2),
        "ราคาขาย/หน่วย": Number(salePricePerUnit).toFixed(2),
        "ราคายืนยัน/หน่วย": Number(confirmPricePerUnit).toFixed(2),
        "ยอดขายก่อนภาษี": Number(beforeVat).toFixed(2),
        "ยอดภาษี": Number(vat).toFixed(2),
        "ยอดรวม": Number(grandTotal).toFixed(2),
        ...(canSeeCost
          ? {
              "ต้นทุนเฉลี่ย": Number(averageCost).toFixed(5),
              "ต้นทุน": Number(costAmount).toFixed(2),
            }
          : {}),
        ...(canSeeProfit
          ? { "กำไรก่อนภาษี": Number(profitBeforeTax).toFixed(2) }
          : {}),
      };
    });

    exportToCSV(dataset, `sales-summary_${startDate}_to_${endDate}.csv`);
  };

  const handleExportRowItems = (row) => {
    const dataset = (row.items || []).flatMap(getDestinationDetailRows).map((p) => {
        const sendW = num(p.sendWeight);
        const destW = num(p.netWeight);
        const deductW = destW - sendW;
        const amountBefore =
        Number(p.amountBeforeVat ?? destW * (p.pricePerUnit ?? 0));
        const vat = Number(p.vat ?? amountBefore * TAX_RATE);
        const total = amountBefore + vat;
        return {
        เลขที่ตั๋ว: row.billNumber ?? "",
        วันที่จ่าย: formatDateTime(row.createdAt),
        ชื่อลูกค้า:
            row.customer?.name ?? row.customerName ?? "-",
        สินค้า: p.productName ?? "-",
        "น้ำหนักส่งสุทธิ": sendW.toFixed(2),
        "น้ำหนักหัก": deductW.toFixed(2),
        "น้ำหนักปลายทางสุทธิ": destW.toFixed(2),
        "ราคาขาย/หน่วย": Number(p.pricePerUnit ?? 0).toFixed(2),
        "ราคายืนยัน/หน่วย": Number(p.confirmPricePerUnit ?? 0).toFixed(2),
        "ยอดขายก่อนภาษี": amountBefore.toFixed(2),
        "ยอดภาษี": vat.toFixed(2),
        "ยอดรวม": total.toFixed(2),
        ...(canSeeCost
          ? {
              "ต้นทุนเฉลี่ย": num(p.averageCost).toFixed(5),
              "ต้นทุน": num(p.costAmount).toFixed(2),
            }
          : {}),
        ...(canSeeProfit
          ? { "กำไรก่อนภาษี": num(p.profitBeforeTax).toFixed(2) }
          : {}),
        };
    });

    exportToCSV(dataset, `ticket_${row.billNumber || row.id || "detail"}.csv`);
  };

  // ---------- Summary (footer) ----------
  const summary = useMemo(() => {
    let ticketCount = sortedRows.length;
    let weightSend = 0;
    let weightDeduct = 0;
    let weightDest = 0;
    let totalBeforeVat = 0;
    let totalVat = 0;
    let totalCost = 0;
    let totalSendAmount = 0;

    sortedRows.forEach((row) => {
      const m = getRowMetrics(row);
      weightSend += m.weightSend;
      weightDeduct += m.weightDeduct;
      weightDest += m.weightDest;
      totalBeforeVat += m.beforeVat;
      totalVat += m.vat;
      totalCost += m.costAmount;
      totalSendAmount += m.sendAmount;
    });

    const grandTotal = totalBeforeVat + totalVat;

    return {
      ticketCount,
      weightSend,
      weightDeduct,
      weightDest,
      totalBeforeVat,
      totalVat,
      grandTotal,
      totalCost,
      totalProfitBeforeTax: totalBeforeVat - totalCost,
      salePricePerUnit:
        weightSend > 0 ? totalSendAmount / weightSend : 0,
      confirmPricePerUnit:
        weightDest > 0 ? totalBeforeVat / weightDest : 0,
    };
  }, [sortedRows]);

  return (
    <div className="min-h-screen bg-gray-50 p-4 font-sans">
      <div className="max-w-7xl mx-auto">
        <div className="mb-6">
          <AppBreadcrumbs />
        </div>

        {/* Header & Controls */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-6">
          <div className="flex flex-col xl:flex-row justify-between items-start xl:items-center gap-4">
             <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2 mb-2 xl:mb-0">
               <span className="w-1 h-8 bg-green-600 rounded-full"></span>
               สรุปยอดขายสินค้ารายตั๋ว
            </h1>

            <div className="flex flex-col lg:flex-row items-start lg:items-center gap-3 w-full xl:w-auto">
                {/* Search */}
               <div className="relative w-full lg:w-64">
                <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                    type="text"
                    placeholder="เลขที่ตั๋ว / ลูกค้า / สินค้า"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none transition-all"
                />
               </div>

               {/* Status Toggle */}
               <div className="flex bg-gray-100 p-1 rounded-xl">
                 {STATUS_OPTIONS.map((opt) => (
                   <button
                     key={opt.value}
                     onClick={() => setSelectedStatus(opt.value)}
                     className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-all ${
                       selectedStatus === opt.value
                         ? "bg-white text-green-700 shadow-sm"
                         : "text-gray-500 hover:text-gray-700"
                     }`}
                   >
                     {opt.label}
                   </button>
                 ))}
               </div>

              {/* Date Pickers */}
              <div className="flex items-center gap-2 bg-gray-50 px-3 py-2 rounded-xl border border-gray-200 w-full lg:w-auto">
                <span className="text-sm font-medium text-gray-500 whitespace-nowrap">เริ่ม</span>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="bg-transparent border-none focus:ring-0 text-gray-700 font-medium text-sm w-full"
                />
              </div>

               <div className="flex items-center gap-2 bg-gray-50 px-3 py-2 rounded-xl border border-gray-200 w-full lg:w-auto">
                <span className="text-sm font-medium text-gray-500 whitespace-nowrap">สิ้นสุด</span>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="bg-transparent border-none focus:ring-0 text-gray-700 font-medium text-sm w-full"
                />
              </div>
            </div>
          </div>
          
           <div className="mt-6 flex gap-2 justify-end">
                 <button
                    onClick={handleExportMain}
                    className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white border border-transparent rounded-xl hover:bg-green-700 transition-all font-medium shadow-sm hover:shadow-md"
                >
                    <ContentCopyIcon fontSize="small" />
                    <span>Export ข้อมูลหลัก</span>
                </button>
          </div>
        </div>

        {/* Content */}
         {loading ? (
             <div className="flex justify-center items-center h-64">
                <div className="w-12 h-12 border-4 border-green-200 border-t-green-600 rounded-full animate-spin"></div>
             </div>
        ) : (
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full min-w-[1800px] text-sm text-left">
                        <thead className="bg-gray-50 text-gray-600 font-semibold border-b border-gray-100">
                             <tr>
                                <th className="px-4 py-4 w-12"></th>
                                <th onClick={() => requestSort("billNumber")} className="px-4 py-4 cursor-pointer hover:bg-gray-100 transition-colors">
                                    <div className="flex items-center gap-1">
                                        เลขที่ตั๋ว {orderBy === "billNumber" && <span className="text-green-600">{order === "asc" ? "↑" : "↓"}</span>}
                                    </div>
                                </th>
                                <th onClick={() => requestSort("createdAt")} className="px-4 py-4 cursor-pointer hover:bg-gray-100 transition-colors">
                                    <div className="flex items-center gap-1">
                                        วันที่จ่าย {orderBy === "createdAt" && <span className="text-green-600">{order === "asc" ? "↑" : "↓"}</span>}
                                    </div>
                                </th>
                                <th onClick={() => requestSort("customerName")} className="px-4 py-4 cursor-pointer hover:bg-gray-100 transition-colors">
                                    <div className="flex items-center gap-1">
                                        ชื่อลูกค้า {orderBy === "customerName" && <span className="text-green-600">{order === "asc" ? "↑" : "↓"}</span>}
                                    </div>
                                </th>
                                <th className="px-4 py-4">สั่งจ่ายโดย</th>
                                <th onClick={() => requestSort("weightSend")} className="px-4 py-4 text-right cursor-pointer hover:bg-gray-100 transition-colors">
                                    <div className="flex items-center justify-end gap-1">
                                        น้ำหนักส่งสุทธิ {orderBy === "weightSend" && <span className="text-green-600">{order === "asc" ? "↑" : "↓"}</span>}
                                    </div>
                                </th>
                                <th onClick={() => requestSort("weightDeduct")} className="px-4 py-4 text-right cursor-pointer hover:bg-gray-100 transition-colors">
                                    <div className="flex items-center justify-end gap-1">
                                        น้ำหนักหัก {orderBy === "weightDeduct" && <span className="text-green-600">{order === "asc" ? "↑" : "↓"}</span>}
                                    </div>
                                </th>
                                <th onClick={() => requestSort("weightDest")} className="px-4 py-4 text-right cursor-pointer hover:bg-gray-100 transition-colors">
                                    <div className="flex items-center justify-end gap-1">
                                        น้ำหนักปลายทางสุทธิ {orderBy === "weightDest" && <span className="text-green-600">{order === "asc" ? "↑" : "↓"}</span>}
                                    </div>
                                </th>
                                <th className="px-4 py-4 text-right whitespace-nowrap">ราคาขาย/หน่วย</th>
                                <th className="px-4 py-4 text-right whitespace-nowrap">ราคายืนยัน/หน่วย</th>
                                <th onClick={() => requestSort("beforeVat")} className="px-4 py-4 text-right cursor-pointer hover:bg-gray-100 transition-colors">
                                    <div className="flex items-center justify-end gap-1">
                                        ยอดขายก่อนภาษี {orderBy === "beforeVat" && <span className="text-green-600">{order === "asc" ? "↑" : "↓"}</span>}
                                    </div>
                                </th>
                                <th onClick={() => requestSort("vat")} className="px-4 py-4 text-right cursor-pointer hover:bg-gray-100 transition-colors">
                                    <div className="flex items-center justify-end gap-1">
                                        ยอดภาษี {orderBy === "vat" && <span className="text-green-600">{order === "asc" ? "↑" : "↓"}</span>}
                                    </div>
                                </th>
                                <th onClick={() => requestSort("grandTotal")} className="px-4 py-4 text-right cursor-pointer hover:bg-gray-100 transition-colors">
                                    <div className="flex items-center justify-end gap-1">
                                        ยอดรวม {orderBy === "grandTotal" && <span className="text-green-600">{order === "asc" ? "↑" : "↓"}</span>}
                                    </div>
                                </th>
                                {canSeeCost && (
                                  <th className="px-4 py-4 text-right whitespace-nowrap">ต้นทุนเฉลี่ย</th>
                                )}
                                {canSeeCost && (
                                  <th onClick={() => requestSort("costAmount")} className="px-4 py-4 text-right cursor-pointer hover:bg-gray-100 transition-colors whitespace-nowrap">
                                    ต้นทุน {orderBy === "costAmount" && <span className="text-green-600">{order === "asc" ? "↑" : "↓"}</span>}
                                  </th>
                                )}
                                {canSeeProfit && (
                                  <th onClick={() => requestSort("profitBeforeTax")} className="px-4 py-4 text-right cursor-pointer hover:bg-gray-100 transition-colors whitespace-nowrap">
                                    กำไรก่อนภาษี {orderBy === "profitBeforeTax" && <span className="text-green-600">{order === "asc" ? "↑" : "↓"}</span>}
                                  </th>
                                )}
                             </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50">
                             {sortedRows.map((row, idx) => {
                                 const key = getRowKey(row, idx);
                                 const isOpen = openSet.has(key);
                                 return (
                                     <RowGroup
                                        key={key}
                                        isOpen={isOpen}
                                        onToggle={() => toggleOpen(key)}
                                        row={row}
                                        formatNum={formatNum}
                                        formatDateTime={formatDateTime}
                                        formatAverageCost={formatAverageCost}
                                        handleExportRowItems={handleExportRowItems}
                                        canSeeCost={canSeeCost}
                                        canSeeProfit={canSeeProfit}
                                     />
                                 );
                             })}
                        </tbody>
                        <tfoot className="bg-gray-50 font-bold text-gray-900 border-t border-gray-200">
                             <tr>
                                <td colSpan={5} className="px-4 py-4 text-right">
                                    รวม {summary.ticketCount.toLocaleString("th-TH")} ตั๋ว
                                </td>
                                <td className="px-4 py-4 text-right">{formatNum(summary.weightSend)}</td>
                                <td className="px-4 py-4 text-right">{formatNum(summary.weightDeduct)}</td>
                                <td className="px-4 py-4 text-right">{formatNum(summary.weightDest)}</td>
                                <td className="px-4 py-4 text-right">{formatNum(summary.salePricePerUnit)}</td>
                                <td className="px-4 py-4 text-right">{formatNum(summary.confirmPricePerUnit)}</td>
                                <td className="px-4 py-4 text-right">{formatNum(summary.totalBeforeVat)}</td>
                                <td className="px-4 py-4 text-right">{formatNum(summary.totalVat)}</td>
                                <td className="px-4 py-4 text-right text-green-700 text-lg">{formatNum(summary.grandTotal)}</td>
                                {canSeeCost && <td className="px-4 py-4 text-right">-</td>}
                                {canSeeCost && <td className="px-4 py-4 text-right">{formatNum(summary.totalCost)}</td>}
                                {canSeeProfit && (
                                  <td className={`px-4 py-4 text-right ${summary.totalProfitBeforeTax < 0 ? "text-red-600" : "text-green-600"}`}>
                                    {formatNum(summary.totalProfitBeforeTax)}
                                  </td>
                                )}
                             </tr>
                        </tfoot>
                    </table>
                </div>
            </div>
        )}
      </div>
    </div>
  );
}

function RowGroup({
  isOpen,
  onToggle,
  row,
  formatNum,
  formatDateTime,
  formatAverageCost,
  handleExportRowItems,
  canSeeCost,
  canSeeProfit,
}) {
    // Calculate metrics for display
    const weightSend = row.items?.reduce((acc, it) => acc + num(it.sendWeight), 0) ?? 0;
    const weightDest = row.items?.reduce((acc, it) => acc + Number(it.netWeight ?? 0), 0) ?? 0;
    const weightDeduct = weightDest - weightSend;
    const beforeVat = row.items?.reduce((acc, it) => acc + Number(it.amountBeforeVat ?? 0), 0) ?? 0;
    const vat = row.items?.reduce((acc, it) => acc + Number(it.vat ?? 0), 0) ?? 0;
    const grandTotal = beforeVat + vat;
    const sendAmount = row.items?.reduce(
      (acc, it) => acc + num(it.sendWeight) * num(it.pricePerUnit),
      0
    ) ?? 0;
    const costAmount = row.items?.reduce((acc, it) => acc + num(it.costAmount), 0) ?? 0;
    const profitBeforeTax = beforeVat - costAmount;
    const averageCost = weightSend > 0 ? costAmount / weightSend : 0;
    const salePricePerUnit = weightSend > 0 ? sendAmount / weightSend : 0;
    const confirmPricePerUnit = weightDest > 0 ? beforeVat / weightDest : 0;
    const columnCount = 13 + (canSeeCost ? 2 : 0) + (canSeeProfit ? 1 : 0);

    return (
        <>
            <tr className="hover:bg-gray-50 transition-colors border-b border-gray-50 last:border-none">
                 <td className="px-4 py-4">
                    <button 
                        onClick={onToggle}
                        className="p-1 rounded-full hover:bg-gray-200 transition-colors text-gray-500"
                    >
                        {isOpen ? <KeyboardArrowDownIcon fontSize="small"/> : <KeyboardArrowRightIcon fontSize="small"/>}
                    </button>
                </td>
                <td className="px-4 py-4 font-medium text-gray-800">{row.billNumber}</td>
                <td className="px-4 py-4 text-gray-600">{formatDateTime(row.createdAt)}</td>
                <td className="px-4 py-4 font-medium text-gray-800">{row.customer?.name ?? row.customerName ?? "-"}</td>
                <td className="px-4 py-4 text-gray-600">
                    {row.openByNames ?? row.orderedBy?.name ?? row.createdBy?.name ?? row.shop?.name ?? "-"}
                </td>
                <td className="px-4 py-4 text-right font-medium">{formatNum(weightSend)}</td>
                <td className="px-4 py-4 text-right text-gray-500">{formatNum(weightDeduct)}</td>
                <td className="px-4 py-4 text-right font-medium text-green-600">{formatNum(weightDest)}</td>
                <td className="px-4 py-4 text-right text-gray-600">{formatNum(salePricePerUnit)}</td>
                <td className="px-4 py-4 text-right text-gray-600">{formatNum(confirmPricePerUnit)}</td>
                <td className="px-4 py-4 text-right text-gray-600">{formatNum(beforeVat)}</td>
                <td className="px-4 py-4 text-right text-gray-600">{formatNum(vat)}</td>
                <td className="px-4 py-4 text-right font-bold text-gray-800">{formatNum(grandTotal)}</td>
                {canSeeCost && <td className="px-4 py-4 text-right text-gray-600">{formatAverageCost(averageCost)}</td>}
                {canSeeCost && <td className="px-4 py-4 text-right font-medium text-gray-800">{formatNum(costAmount)}</td>}
                {canSeeProfit && (
                  <td className={`px-4 py-4 text-right font-bold ${profitBeforeTax < 0 ? "text-red-600" : "text-green-600"}`}>
                    {formatNum(profitBeforeTax)}
                  </td>
                )}
            </tr>

            {isOpen && (
                <tr className="bg-gray-50/50">
                    <td colSpan={columnCount} className="px-4 py-4">
                        <div className="pl-12 pr-4">
                             <div className="flex justify-between items-center mb-3">
                                <h4 className="text-sm font-bold text-gray-700 flex items-center gap-2">
                                    <span className="w-1 h-4 bg-green-500 rounded-full"></span>
                                    รายการสินค้า
                                </h4>
                                <button
                                    onClick={() => handleExportRowItems(row)}
                                    className="flex items-center gap-1 text-xs text-green-600 hover:text-green-700 hover:underline border border-green-200 bg-white px-2 py-1 rounded"
                                >
                                    <ContentCopyIcon style={{ fontSize: 14 }} />
                                    Export ข้อมูลย่อย
                                </button>
                            </div>

                             <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                                 <table className="w-full text-sm text-left">
                                     <thead className="bg-gray-50 text-gray-500 border-b border-gray-200">
                                         <tr>
                                            <th className="px-4 py-2 font-medium">สินค้า</th>
                                            <th className="px-4 py-2 text-right font-medium">น้ำหนักส่ง</th>
                                            <th className="px-4 py-2 text-right font-medium">หัก</th>
                                            <th className="px-4 py-2 text-right font-medium">สุทธิ</th>
                                            <th className="px-4 py-2 text-right font-medium">ราคาขาย</th>
                                            <th className="px-4 py-2 text-right font-medium">ราคายืนยัน</th>
                                            <th className="px-4 py-2 text-right font-medium">ก่อนภาษี</th>
                                            <th className="px-4 py-2 text-right font-medium">ภาษี</th>
                                            <th className="px-4 py-2 text-right font-medium">ยอดรวม</th>
                                            {canSeeCost && <th className="px-4 py-2 text-right font-medium">ต้นทุนเฉลี่ย</th>}
                                            {canSeeCost && <th className="px-4 py-2 text-right font-medium">ต้นทุน</th>}
                                            {canSeeProfit && <th className="px-4 py-2 text-right font-medium">กำไรก่อนภาษี</th>}
                                         </tr>
                                     </thead>
                                     <tbody className="divide-y divide-gray-100">
                                         {(row.items || []).flatMap(getDestinationDetailRows).map((p, i) => {
                                              const sendW = num(p.sendWeight);
                                              const destW = Number(p.netWeight ?? 0);
                                              const deductW = destW - sendW;
                                              const amountBefore = Number(p.amountBeforeVat ?? destW * (p.pricePerUnit ?? 0));
                                              const vat = Number(p.vat ?? amountBefore * TAX_RATE);
                                              const total = amountBefore + vat;
                                              const profitBeforeTax = amountBefore - num(p.costAmount);

                                             return (
                                                 <tr key={p.detailKey ?? p.id ?? i} className="hover:bg-gray-50">
                                                    <td className="px-4 py-2 text-gray-800">
                                                      <div>{p.productName ?? "-"}</div>
                                                      {p.sourceProductName && p.sourceProductName !== p.productName && (
                                                        <div className="text-xs text-gray-400">จากสินค้า {p.sourceProductName}</div>
                                                      )}
                                                    </td>
                                                    <td className="px-4 py-2 text-right text-gray-600">{formatNum(sendW)}</td>
                                                    <td className="px-4 py-2 text-right text-gray-600">{formatNum(deductW)}</td>
                                                    <td className="px-4 py-2 text-right font-medium text-gray-800">{formatNum(destW)}</td>
                                                    <td className="px-4 py-2 text-right text-gray-600">{formatNum(p.pricePerUnit)}</td>
                                                    <td className="px-4 py-2 text-right text-gray-600">{formatNum(p.confirmPricePerUnit)}</td>
                                                    <td className="px-4 py-2 text-right text-gray-600">{formatNum(amountBefore)}</td>
                                                    <td className="px-4 py-2 text-right text-gray-600">{formatNum(vat)}</td>
                                                    <td className="px-4 py-2 text-right font-medium text-gray-800">{formatNum(total)}</td>
                                                    {canSeeCost && <td className="px-4 py-2 text-right text-gray-600">{formatAverageCost(p.averageCost)}</td>}
                                                    {canSeeCost && <td className="px-4 py-2 text-right font-medium text-gray-800">{formatNum(p.costAmount)}</td>}
                                                    {canSeeProfit && (
                                                      <td className={`px-4 py-2 text-right font-medium ${profitBeforeTax < 0 ? "text-red-600" : "text-green-600"}`}>
                                                        {formatNum(profitBeforeTax)}
                                                      </td>
                                                    )}
                                                 </tr>
                                             );
                                         })}
                                     </tbody>
                                 </table>
                             </div>
                        </div>
                    </td>
                </tr>
            )}
        </>
    );
}
